import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Users, Leaf, Heart, Award } from 'lucide-react';
import axios from 'axios';

// ============================================================
// ALDRIN'S RESTAURANT - PAGE À PROPOS
// Design: Élégant, professionnel, lisible
// ============================================================

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_resto-stock-manager-3/artifacts/0l7b747s_9F91CBF8-DC1E-49E7-9C24-96206851AEA7.PNG';
const FOUNDER_PHOTO = 'https://customer-assets.emergentagent.com/job_resto-stock-manager-3/artifacts/xzs8u5pw_AA373550-2B5A-491C-937C-1B4F11CD8B84.jpg';
const BRAND_GREEN = '#1a3d2c';

// Contenu par défaut
const DEFAULT_ABOUT_CONTENT = `Chez Aldrin's Restaurant, nous faisons le choix de l'authenticité et de la maîtrise.

Nous travaillons en priorité avec des produits issus des côtes gabonaises et valorisons autant que possible les ressources locales. Cette approche n'est pas seulement un engagement, c'est une manière de soutenir notre environnement, notre économie et de garantir la fraîcheur de nos plats.

Notre réalité quotidienne est simple : produire mieux, avec rigueur.
La maîtrise des coûts de production fait partie intégrante de notre fonctionnement, afin de proposer une cuisine accessible sans compromis sur la qualité.

Notre équipe est composée majoritairement de professionnels gabonais, engagés, formés et responsables. Nous croyons en la compétence locale et en la capacité de chacun à évoluer dans un cadre structuré et exigeant.

Chez Aldrin's, le sens du détail est une priorité.
Chaque élément compte : la préparation, la présentation, l'organisation, le service.

Nous nous attachons à rester compétitifs, tout en maintenant un niveau d'exigence élevé.
L'hygiène n'est pas une option, c'est une base.
L'accueil, le sourire et le respect du client font partie de notre identité.

Notre ambition est claire : offrir une expérience simple, propre, maîtrisée et constante.

Aldrin's Restaurant, c'est une cuisine locale assumée, un service structuré, et une exigence quotidienne.`;

const AboutPage = () => {
    const [content, setContent] = useState(DEFAULT_ABOUT_CONTENT);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/client/pages/about`);
                if (response.data?.content) {
                    setContent(response.data.content);
                }
            } catch (error) {
                console.log('Using default about content');
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);
    
    // Valeurs de l'entreprise
    const values = [
        { icon: Leaf, title: 'Produits locaux', description: 'Côtes gabonaises et ressources locales' },
        { icon: Users, title: 'Équipe locale', description: 'Professionnels gabonais engagés' },
        { icon: Heart, title: 'Qualité', description: 'Exigence quotidienne sans compromis' },
        { icon: Award, title: 'Excellence', description: 'Service structuré et professionnel' }
    ];
    
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Header */}
            <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <Link 
                            to="/client" 
                            className="flex items-center gap-1.5 text-sm transition-colors hover:opacity-70"
                            style={{ color: BRAND_GREEN }}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Retour
                        </Link>
                        
                        <Link to="/client">
                            <img 
                                src={LOGO_URL} 
                                alt="Aldrin Restaurant" 
                                className="h-10 sm:h-12"
                            />
                        </Link>
                        
                        <div className="w-16" />
                    </div>
                </div>
            </header>
            
            {/* Hero Section */}
            <section 
                className="py-16 sm:py-20 text-center"
                style={{ backgroundColor: `${BRAND_GREEN}08` }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-4xl mx-auto px-4"
                >
                    <h1 
                        className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
                        style={{ color: BRAND_GREEN }}
                    >
                        À propos de nous
                    </h1>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                        Découvrez l'histoire et les valeurs qui font d'Aldrin's Restaurant 
                        un établissement unique à Libreville
                    </p>
                </motion.div>
            </section>
            
            {/* Founder Section */}
            <section className="py-12 sm:py-16 bg-white">
                <div className="max-w-4xl mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="flex flex-col md:flex-row items-center gap-8 md:gap-12"
                    >
                        {/* Photo du fondateur */}
                        <div className="flex-shrink-0">
                            <div 
                                className="relative w-56 h-56 sm:w-64 sm:h-64 rounded-full overflow-hidden shadow-2xl border-4"
                                style={{ borderColor: BRAND_GREEN }}
                            >
                                <img 
                                    src={FOUNDER_PHOTO}
                                    alt="ALDRIN'S - Fondateur"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                        
                        {/* Info fondateur */}
                        <div className="text-center md:text-left flex-1">
                            <h2 
                                className="text-2xl sm:text-3xl font-bold mb-2"
                                style={{ color: BRAND_GREEN }}
                            >
                                ALDRIN'S
                            </h2>
                            <p className="text-lg text-gray-500 mb-4 font-medium">
                                Fondateur & Directeur Général
                            </p>
                            <div className="w-16 h-1 rounded-full mx-auto md:mx-0 mb-4" style={{ backgroundColor: BRAND_GREEN }}></div>
                            <p className="text-gray-600 leading-relaxed">
                                Passionné par la gastronomie et l'excellence du service, ALDRIN'S a fondé ce restaurant 
                                avec une vision claire : offrir à Libreville une expérience culinaire authentique, 
                                valorisant les produits locaux gabonais avec un service professionnel de qualité.
                            </p>
                            <p className="text-gray-600 leading-relaxed mt-3 italic">
                                "Notre engagement est simple : la qualité, l'authenticité et le respect de nos clients."
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>
            
            {/* Values Section */}
            <section className="py-12 bg-white">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {values.map((value, index) => {
                            const Icon = value.icon;
                            return (
                                <motion.div
                                    key={value.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    className="text-center p-4"
                                >
                                    <div 
                                        className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
                                        style={{ backgroundColor: `${BRAND_GREEN}10` }}
                                    >
                                        <Icon className="w-7 h-7" style={{ color: BRAND_GREEN }} />
                                    </div>
                                    <h3 className="font-semibold text-gray-800 mb-1">{value.title}</h3>
                                    <p className="text-sm text-gray-500">{value.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>
            
            {/* Main Content */}
            <main className="py-12 sm:py-16">
                <motion.article
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="max-w-3xl mx-auto px-4 sm:px-6"
                >
                    <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 sm:p-12">
                        {loading ? (
                            <div className="animate-pulse space-y-4">
                                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                <div className="h-4 bg-gray-200 rounded w-full"></div>
                                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                            </div>
                        ) : (
                            <div className="prose prose-lg max-w-none">
                                {content.split('\n\n').map((paragraph, index) => (
                                    <p 
                                        key={index} 
                                        className="text-gray-700 leading-relaxed mb-6 last:mb-0"
                                        style={{ 
                                            fontSize: '1.1rem',
                                            lineHeight: '1.85'
                                        }}
                                    >
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* Signature */}
                    <div className="text-center mt-10">
                        <img 
                            src={LOGO_URL} 
                            alt="Aldrin's" 
                            className="h-16 mx-auto mb-3 opacity-60"
                        />
                        <p className="text-sm text-gray-400 italic">
                            "Une cuisine locale assumée, un service structuré, une exigence quotidienne."
                        </p>
                    </div>
                </motion.article>
            </main>
            
            {/* Footer */}
            <footer className="bg-gray-50 border-t border-gray-100 py-8">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <div className="flex flex-wrap justify-center gap-6 text-sm">
                        <Link to="/client" className="text-gray-500 hover:text-gray-700 transition-colors">
                            Accueil
                        </Link>
                        <Link to="/client/menu" className="text-gray-500 hover:text-gray-700 transition-colors">
                            Notre Carte
                        </Link>
                        <Link to="/client/reservation" className="text-gray-500 hover:text-gray-700 transition-colors">
                            Réservation
                        </Link>
                        <Link to="/client/about" className="font-medium" style={{ color: BRAND_GREEN }}>
                            À propos
                        </Link>
                        <Link to="/client/terms" className="text-gray-500 hover:text-gray-700 transition-colors">
                            CGV
                        </Link>
                    </div>
                    <p className="text-gray-400 text-sm mt-6">
                        © {new Date().getFullYear()} Aldrin's Restaurant. Tous droits réservés.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default AboutPage;
